import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  apiClient as defaultClient,
  type ApiClient,
  type DefectDetail as DefectDetailData,
  type DefectReviewHistoryEntry,
  type DefectSummary,
  type HumanReviewState,
  type ReviewState,
  type ReviewDefectRequest,
} from '../api/client';
import { isApiError } from '../api/errors';
import { AppShell } from '../components/AppShell';
import { Card } from '../components/Card';
import { Spinner } from '../components/Spinner';
import { Button } from '../components/Button';
import { InlineAlert } from '../components/InlineAlert';
import { StatusBadge } from '../components/StatusBadge';
import { FormField } from '../components/FormField';
import { SelectField, type SelectOption } from '../components/SelectField';
import { useOrg } from '../org/context';
import { useFocusMain } from '../a11y/useFocusMain';
import { AuthedBrand } from './AuthedBrand';
import { journeyTypeLabel } from './journeyLabels';
import {
  describeDetailReviewState,
  describeReasonCode,
  HUMAN_REVIEW_STATES,
  REVIEW_STATE_LABELS,
  REVIEW_TARGET_LABELS,
  canonicalRequired,
  noteRequired,
  reviewErrorCopy,
  type HumanReviewState as LabelReviewState,
} from './defectLabels';
import styles from './DefectDetail.module.css';

type LoadState = 'loading' | 'error' | 'ready';

const NOTE_MAX = 4000;

export interface DefectDetailProps {
  client?: ApiClient;
}

function formatTime(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString();
}

/** Option label for a canonical-defect candidate — enough to disambiguate. */
function canonicalOptionLabel(d: DefectSummary): string {
  return `${d.severity} · ${journeyTypeLabel(d.journeyType)} · Step ${d.stepOrder}`;
}

/**
 * Defect-detail view + review controls (Story #38, slice 38b). Renders one
 * defect's honest facts and lets a reviewer transition it into one of the six
 * human states via the 38a review endpoint. Honest by construction: a defect is
 * *potential* until a person sets `confirmed`; "Confirmed" copy never appears
 * otherwise. Optimistic-concurrency via `expectedState`; a 409 silently re-fetches
 * and asks the reviewer to re-check. The review-history timeline (#39) reflects the
 * append-only audit trail and re-fetches after each review.
 */
export function DefectDetail({ client = defaultClient }: DefectDetailProps) {
  const { currentOrg } = useOrg();
  const { id, defectId } = useParams<{ id: string; defectId: string }>();
  useFocusMain();

  const orgId = currentOrg?.id;
  const canReview = currentOrg?.role !== 'viewer';

  const [phase, setPhase] = useState<LoadState>('loading');
  const [loadMessage, setLoadMessage] = useState('');
  const [data, setData] = useState<DefectDetailData | null>(null);
  const [history, setHistory] = useState<DefectReviewHistoryEntry[] | null>(null);

  const [targetState, setTargetState] = useState<HumanReviewState | ''>('');
  const [note, setNote] = useState('');
  const [canonicalId, setCanonicalId] = useState('');
  const [candidates, setCandidates] = useState<DefectSummary[]>([]);

  const [submitBusy, setSubmitBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [formErrorRef, setFormErrorRefState] = useState('');
  const [noteError, setNoteError] = useState<string | null>(null);
  const [canonicalError, setCanonicalError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const successRef = useRef<HTMLDivElement>(null);
  const formErrorAlertRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    if (!orgId || !id || !defectId) return;
    setPhase('loading');
    try {
      const next = await client.getDefect(orgId, id, defectId);
      setData(next);
      setPhase('ready');
    } catch (err) {
      const status = isApiError(err) ? err.status : 0;
      setLoadMessage(
        status === 404 ? "We couldn't find this defect." : 'Something went wrong. Please try again.',
      );
      setPhase('error');
    }
  }, [client, orgId, id, defectId]);

  useEffect(() => {
    void load();
  }, [load]);

  const loadHistory = useCallback(async () => {
    if (!orgId || !id || !defectId) return;
    try {
      const res = await client.getDefectReviewHistory(orgId, id, defectId);
      setHistory(res.reviews);
    } catch {
      // History is a secondary panel; a failure here must not break the page.
      setHistory([]);
    }
  }, [client, orgId, id, defectId]);

  useEffect(() => {
    void loadHistory();
  }, [loadHistory]);

  // Lazily load canonical candidates when the reviewer chooses "duplicate".
  useEffect(() => {
    if (targetState !== 'duplicate' || !orgId || !id || !defectId) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await client.defectsList(orgId, id, { limit: 200 });
        if (cancelled) return;
        setCandidates(
          res.defects.filter((d) => d.id !== defectId && d.reviewState !== 'duplicate'),
        );
      } catch {
        if (!cancelled) setCandidates([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, orgId, id, defectId, targetState]);

  function selectTarget(next: HumanReviewState) {
    setTargetState(next);
    setNoteError(null);
    setCanonicalError(null);
    setFormError(null);
    if (next !== 'duplicate') setCanonicalId('');
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!orgId || !id || !defectId || !data || submitBusy || targetState === '') return;

    const trimmedNote = note.trim();
    if (noteRequired(data.severity, targetState) && trimmedNote.length === 0) {
      setNoteError('Add a note explaining why you are ignoring this critical or high defect.');
      document.getElementById('dd-note')?.focus();
      return;
    }
    if (canonicalRequired(targetState) && canonicalId === '') {
      setCanonicalError('Choose which defect this is a duplicate of.');
      document.getElementById('dd-canonical')?.focus();
      return;
    }
    if (targetState === data.reviewState) {
      setFormError('This defect is already in that state. Choose a different one.');
      setFormErrorRefState('');
      window.requestAnimationFrame(() => formErrorAlertRef.current?.focus());
      return;
    }

    setSubmitBusy(true);
    setFormError(null);
    setNoteError(null);
    setCanonicalError(null);
    setSuccessMessage(null);

    const body: ReviewDefectRequest = {
      toState: targetState,
      expectedState: data.reviewState as ReviewState,
    };
    if (trimmedNote.length > 0) body.note = trimmedNote;
    if (targetState === 'duplicate') body.canonicalDefectId = canonicalId;

    try {
      const result = await client.reviewDefect(orgId, id, defectId, body);
      const fresh = await client.getDefect(orgId, id, defectId);
      setData(fresh);
      void loadHistory();
      setTargetState('');
      setNote('');
      setCanonicalId('');
      setSuccessMessage(
        `Review saved. This defect is now marked “${REVIEW_STATE_LABELS[result.reviewState]}”.`,
      );
      window.requestAnimationFrame(() => successRef.current?.focus());
    } catch (err) {
      const status = isApiError(err) ? err.status : 0;
      const token = isApiError(err) ? err.message : '';
      const requestId = isApiError(err) ? err.requestId : undefined;
      const copy = reviewErrorCopy(status, token, requestId);

      if (copy.conflict) {
        try {
          const fresh = await client.getDefect(orgId, id, defectId);
          setData(fresh);
        } catch {
          /* keep the stale view; the conflict message still prompts a retry */
        }
      }

      if (copy.placement === 'note') {
        setNoteError(copy.message);
        document.getElementById('dd-note')?.focus();
      } else if (copy.placement === 'canonical') {
        setCanonicalError(copy.message);
        document.getElementById('dd-canonical')?.focus();
      } else {
        setFormError(copy.message);
        setFormErrorRefState(copy.requestId ?? '');
        window.requestAnimationFrame(() => formErrorAlertRef.current?.focus());
      }
    } finally {
      setSubmitBusy(false);
    }
  }

  const canonicalOptions: SelectOption[] = [
    { value: '', label: 'Choose a defect…' },
    ...candidates.map((d) => ({ value: d.id, label: canonicalOptionLabel(d) })),
  ];

  return (
    <AppShell brand={<AuthedBrand />}>
      <Card>
        <p className={styles.back}>
          <Link to={`/projects/${id ?? ''}/defects`}>Back to defects</Link>
        </p>

        {phase === 'loading' ? (
          <div className={styles.loading}>
            <Spinner />
            <span>Loading defect&hellip;</span>
          </div>
        ) : null}

        {phase === 'error' ? (
          <div className={styles.loadError}>
            <InlineAlert severity="error">{loadMessage}</InlineAlert>
            <Link to={`/projects/${id ?? ''}/defects`} className={styles.backLink}>
              Back to defects
            </Link>
          </div>
        ) : null}

        {phase === 'ready' && data ? (
          <>
            <div className={styles.titleRow}>
              <h1 id="dd-heading" className={styles.heading}>
                {data.journeyTitle} — defect
              </h1>
              <StatusBadge kind="severity" status={data.severity} />
              <span className={styles.stateChip}>{describeDetailReviewState(data.reviewState)}</span>
            </div>
            <p className={styles.subheading}>
              {data.isPotential
                ? 'This is a potential defect. It stays potential until a person confirms it — TestCraft never decides.'
                : 'A person has reviewed this defect.'}
            </p>

            <section className={styles.section} aria-labelledby="dd-facts-heading">
              <h2 id="dd-facts-heading" className={styles.sectionHeading}>
                Details
              </h2>
              <dl className={styles.facts}>
                <dt>State</dt>
                <dd>{describeDetailReviewState(data.reviewState)}</dd>
                <dt>Journey</dt>
                <dd>
                  {journeyTypeLabel(data.journeyType)}
                  <span className={styles.secondary}>{data.journeyTitle}</span>
                </dd>
                <dt>Failing step</dt>
                <dd>
                  Step {data.stepOrder}
                  <span className={styles.secondary}>{describeReasonCode(data.reasonCode)}</span>
                </dd>
                <dt>Occurrences</dt>
                <dd>{data.occurrenceCount}</dd>
                <dt>First detected</dt>
                <dd>{formatTime(data.firstDetectedAt) ?? '—'}</dd>
                <dt>Last detected</dt>
                <dd>{formatTime(data.lastDetectedAt) ?? '—'}</dd>
                {data.reviewState === 'duplicate' && data.canonicalDefectId ? (
                  <>
                    <dt>Duplicate of</dt>
                    <dd>
                      <Link to={`/projects/${id ?? ''}/defects/${data.canonicalDefectId}`}>
                        View canonical defect
                      </Link>
                    </dd>
                  </>
                ) : null}
              </dl>
              <p className={styles.actions}>
                <Link
                  to={`/projects/${id ?? ''}/journey-health/${data.journeyType}/execution`}
                  aria-label={`View run evidence for ${journeyTypeLabel(data.journeyType)} defect`}
                >
                  View run evidence
                </Link>
              </p>
            </section>

            <section className={styles.section} aria-labelledby="dd-review-heading">
              <h2 id="dd-review-heading" className={styles.sectionHeading}>
                Review this defect
              </h2>

              <InlineAlert ref={successRef} severity="success">
                {successMessage}
              </InlineAlert>

              {canReview ? (
                <form className={styles.form} onSubmit={submit} noValidate>
                  <InlineAlert
                    ref={formErrorAlertRef}
                    severity="error"
                    {...(formErrorRef ? { requestId: formErrorRef } : {})}
                  >
                    {formError}
                  </InlineAlert>

                  <fieldset className={styles.stateGroup}>
                    <legend>New state</legend>
                    {HUMAN_REVIEW_STATES.map((s) => (
                      <label key={s} className={styles.radioRow}>
                        <input
                          type="radio"
                          name="target-state"
                          value={s}
                          checked={targetState === s}
                          onChange={() => selectTarget(s)}
                        />
                        <span>{REVIEW_TARGET_LABELS[s as LabelReviewState]}</span>
                      </label>
                    ))}
                  </fieldset>

                  <FormField
                    id="dd-note"
                    label={
                      data.severity === 'critical' || data.severity === 'high'
                        ? targetState === 'ignored'
                          ? 'Note (required)'
                          : 'Note (optional)'
                        : 'Note (optional)'
                    }
                    {...(noteError ? { error: noteError } : {})}
                  >
                    {({ controlProps }) => (
                      <textarea
                        {...controlProps}
                        className={styles.note}
                        value={note}
                        maxLength={NOTE_MAX}
                        rows={3}
                        aria-required={targetState === 'ignored' && noteRequired(data.severity, 'ignored')}
                        onChange={(e) => setNote(e.target.value)}
                      />
                    )}
                  </FormField>

                  {targetState === 'duplicate' ? (
                    <SelectField
                      id="dd-canonical"
                      label="Canonical defect (required)"
                      value={canonicalId}
                      onChange={setCanonicalId}
                      options={canonicalOptions}
                      {...(canonicalError ? { error: canonicalError } : {})}
                    />
                  ) : null}

                  <div className={styles.formActions}>
                    <Button type="submit" busy={submitBusy}>
                      {submitBusy ? 'Saving…' : 'Save review'}
                    </Button>
                  </div>
                </form>
              ) : (
                <InlineAlert severity="info">
                  You don&rsquo;t have permission to review defects.
                </InlineAlert>
              )}
            </section>

            <section className={styles.section} aria-labelledby="dd-history-heading">
              <h2 id="dd-history-heading" className={styles.sectionHeading}>
                Review history
              </h2>
              {history && history.length > 0 ? (
                <ol className={styles.timeline}>
                  {history.map((entry) => (
                    <li key={entry.id} className={styles.timelineItem}>
                      <div className={styles.timelineHead}>
                        <span className={styles.timelineActor}>
                          {entry.actorType === 'system' ? 'System (automatic)' : 'Reviewer'}
                        </span>
                        <span className={styles.timelineTransition}>
                          {describeDetailReviewState(entry.fromState)} &rarr;{' '}
                          {describeDetailReviewState(entry.toState)}
                        </span>
                        <span className={styles.timelineTime}>
                          {formatTime(entry.createdAt) ?? '—'}
                        </span>
                      </div>
                      {entry.note ? <p className={styles.timelineNote}>{entry.note}</p> : null}
                    </li>
                  ))}
                </ol>
              ) : (
                <p className={styles.timelineEmpty}>
                  No review activity yet — this defect is still in its initial state.
                </p>
              )}
            </section>
          </>
        ) : null}
      </Card>
    </AppShell>
  );
}
